import { Container } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useAPI } from "../../apiv1";
import { useAppDispatch } from "../../store/hooks";
import { changeToNewOrganization } from "../../store/thunk/auth";
import { Invite, Error as APIError } from "../../types/apiv1";
import InviteCard, { InviteError } from "../ui/InviteCard";

function useInvite(inviteID: string) {
	const api = useAPI();
	const dispatch = useAppDispatch();
	const [data, setData] = useState<Invite | null>(null);
	const [loadError, setLoadError] = useState<InviteError | null>(null);
	const [acceptError, setAcceptError] = useState<InviteError | null>(null);

	useEffect(() => {
		setLoadError(null);
		setAcceptError(null);
		api.users.me.invites.id(inviteID).get()
			.then(invite => setData(invite))
			.catch(error => {
				switch (error) {
				case APIError.NotFound:
					setLoadError(InviteError.NotFound);
					break;
				case APIError.Forbidden:
					setLoadError(InviteError.Forbidden);
					break;
				case APIError.Network:
					setLoadError(InviteError.Network);
					break;
				default:
					setLoadError(InviteError.Bug);
				}
			});
	}, [inviteID, setData, setLoadError, setAcceptError, api]);

	const accept = useCallback(() => {
		if (!data) return;
		api.users.me.invites.id(data.invite_id).post()
			.then(() => dispatch(changeToNewOrganization(data.organization.organization_id, "/")))
			.catch(error => {
				switch (error) {
				case APIError.NotFound:
					setAcceptError(InviteError.NotFound);
					break;
				case APIError.Forbidden:
					setAcceptError(InviteError.Forbidden);
					break;
				case APIError.Network:
					setAcceptError(InviteError.Network);
					break;
				default:
					setAcceptError(InviteError.Bug);
				}
			});
	}, [data, api, setAcceptError, dispatch])

	return { data, loadError, accept, acceptError };
}

export default function InvitePage() {
	const { inviteID } = useParams() as { inviteID: string };
	const invite = useInvite(inviteID);

	return (
		<Container maxWidth="sm" sx={{ marginTop: 3, marginBottom: 3}}>
			<InviteCard
				loadError={invite.loadError}
				acceptError={invite.acceptError}
				invite={invite.data}
				onAccept={invite.accept}
			/>
		</Container>
	);
}
